'use client';

import { useState, useCallback } from 'react';
import { Link2, Check } from 'lucide-react';
import { trackToolResultAction } from '@/lib/analytics';

interface ShareableLinkButtonProps {
  /** Tool slug for tracking */
  toolSlug: string;
  /** Button label (default: "Copy shareable link") */
  label?: string;
  /** Additional className */
  className?: string;
  /** Button size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Button style variant */
  variant?: 'default' | 'outline' | 'ghost';
}

/**
 * Copies the current page URL (including deep-link query params) to the
 * clipboard so a user can share a pre-filled tool permalink.
 *
 * R2-bundle safe: only touches window.location and navigator.clipboard.
 * Pair with the `url-state` library so tool inputs round-trip through the URL.
 */
export default function ShareableLinkButton({
  toolSlug,
  label = 'Copy shareable link',
  className = '',
  size = 'md',
  variant = 'outline',
}: ShareableLinkButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(async () => {
    if (typeof window === 'undefined') return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      trackToolResultAction(toolSlug, 'share');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy shareable link:', error);
    }
  }, [toolSlug]);

  // Size classes
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs gap-1',
    md: 'px-3 py-1.5 text-sm gap-1.5',
    lg: 'px-4 py-2 text-base gap-2',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  // Variant classes
  const variantClasses = {
    default: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
    outline: 'border border-gray-300 hover:bg-gray-50 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-600',
  };

  return (
    <button
      type="button"
      onClick={handleShare}
      className={`inline-flex items-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}
      title={copied ? 'Link copied!' : 'Copy a shareable link to this result'}
    >
      {copied ? (
        <>
          <Check className={`${iconSizes[size]} text-green-600`} />
          <span className="text-green-600">Link copied!</span>
        </>
      ) : (
        <>
          <Link2 className={iconSizes[size]} />
          <span>{label}</span>
        </>
      )}
    </button>
  );
}
